import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text4 } from '@osmosis/tailwind-ui/styles';
import Image from 'next/image';
import { MouseEvent, useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Reuse the approved confused-Mo art already tracked in the monorepo until the Jira export can be placed in this app.
import confusedMo from '../../../mobile/assets/images/characters/mo-confused.png';
import { track } from '../../lib/util/tracking';
import Modal, { ModalProps } from '../Modal';
import { useTrialAbuseRestrictionCoordinator } from './TrialAbuseRestrictionCoordinator';

export const TRIAL_ABUSE_UPGRADE_URL = 'https://www.osmosis.org/plans';
export const TRIAL_ABUSE_SUPPORT_URL = 'https://www.elsevier.support/osmosis';
const FALLBACK_ILLUSTRATION = '😕';

type Props = Readonly<Omit<ModalProps, 'children'>>;

/**
 * Explains why trial access is paused and provides upgrade and support actions.
 */
export default function TrialAbuseRestrictionModal({
  hide,
  isVisible,
  ...modalProps
}: Props) {
  const [imageFailed, setImageFailed] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const requestId = useId();
  const coordinator = useTrialAbuseRestrictionCoordinator();
  const setRequestVisible = coordinator?.setRequestVisible;
  const intl = useIntl();
  const confusedMoLabel = intl.formatMessage({
    id: 'trialAbuseRestriction.imageAlt',
    defaultMessage: 'Confused Mo',
  });

  useEffect(() => {
    setRequestVisible?.(requestId, isVisible);

    return () => setRequestVisible?.(requestId, false);
  }, [isVisible, requestId, setRequestVisible]);

  const coordinatedIsVisible = coordinator
    ? isVisible && coordinator.activeRequestId === requestId
    : isVisible;

  useEffect(() => {
    if (!coordinatedIsVisible) return;
    closeButtonRef.current?.focus();
    track('trial_abuse_restriction_impression', {}, ['keen', 'pendo', 'gtm']);
  }, [coordinatedIsVisible]);

  const close = () => {
    if (!coordinatedIsVisible) return;

    coordinator?.dismissVisibleRequests();
    track('trial_abuse_restriction_dismiss', {}, ['keen', 'pendo', 'gtm']);
    hide();
  };

  const trackAction =
    (action: 'upgrade' | 'support') => (event: MouseEvent) => {
      event.stopPropagation();
      track(`trial_abuse_restriction_${action}`, {}, ['keen', 'pendo', 'gtm']);
    };

  const paragraphClass = cn('text-base leading-relaxed', text4);

  return (
    <Modal
      {...modalProps}
      isVisible={coordinatedIsVisible}
      hide={close}
      role="dialog"
      aria-modal
      _className="!w-full !max-w-xl"
    >
      <Box className="relative px-8 py-10 text-center">
        <Box className="absolute right-4 top-4">
          <IconButton
            ref={closeButtonRef}
            ariaLabel={intl.formatMessage({
              id: 'trialAbuseRestriction.close',
              defaultMessage: 'Close',
            })}
            variant="transparent"
            icon={<Icon icon={faTimes} />}
            onClick={close}
            data-testid="trial-abuse-restriction-close"
          />
        </Box>
        <Box className="mx-auto mb-5 flex h-28 items-center justify-center">
          {imageFailed ? (
            <>
              <span aria-hidden="true" className="text-5xl">
                {FALLBACK_ILLUSTRATION}
              </span>
              <span className="sr-only">{confusedMoLabel}</span>
            </>
          ) : (
            <Image
              src={confusedMo}
              alt={confusedMoLabel}
              width={180}
              height={141}
              className="h-auto max-h-28 w-auto"
              onError={() => setImageFailed(true)}
            />
          )}
        </Box>
        <H2 className="mb-4 text-2xl font-bold">
          <FormattedMessage
            id="trialAbuseRestriction.title"
            defaultMessage="Your trial access is paused"
          />
        </H2>
        <Box className="mx-auto max-w-md space-y-4">
          <P
            className={paragraphClass}
            data-testid="trial-abuse-restriction-paragraph"
          >
            <FormattedMessage
              id="trialAbuseRestriction.reason"
              defaultMessage="We noticed usage that goes beyond what's included in a free trial, so we've paused your account for now."
            />
          </P>
          <P
            className={paragraphClass}
            data-testid="trial-abuse-restriction-paragraph"
          >
            <FormattedMessage
              id="trialAbuseRestriction.nextSteps"
              defaultMessage="Upgrade to a paid plan for uninterrupted access. If this looks like a mistake, contact our team and we'll take a look."
            />
          </P>
        </Box>
        <Flex
          className="mt-7 flex-wrap justify-center gap-3"
          data-testid="trial-abuse-restriction-actions"
        >
          <Button
            as="a"
            color="blue"
            rounded
            href={TRIAL_ABUSE_UPGRADE_URL}
            onClick={trackAction('upgrade')}
            data-testid="trial-abuse-restriction-upgrade"
          >
            <FormattedMessage
              id="trialAbuseRestriction.upgrade"
              defaultMessage="Upgrade now"
            />
          </Button>
          <Button
            as="a"
            color="blue"
            variant="transparent"
            rounded
            href={TRIAL_ABUSE_SUPPORT_URL}
            onClick={trackAction('support')}
            data-testid="trial-abuse-restriction-support"
          >
            <FormattedMessage
              id="trialAbuseRestriction.support"
              defaultMessage="Contact support"
            />
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
}
